import type { ScenarioSpec } from "./types";

/**
 * Scenario: "Whaling with a Deepfake Executive".
 *
 * The learner (Michael, a finance manager) is on holiday. A spoofed CEO email
 * primes a live deepfake video call from "Emma" (the CEO), who pressures him to
 * approve an urgent, confidential €850,000 wire to a brand-new vendor. The video
 * is a CONTROLLED deepfake with inspectable tells; the decision (approve /
 * challenge / verify) is the branching point, each with its own debrief.
 *
 * Asset URLs point at our own VPS (`/cdn/sim/deepfake-whaling/*`). Until the
 * Marble hotel world is normalized the loader falls back to the procedural
 * environment, so this runs even with the splat absent.
 */
export const deepfakeWhalingScenario: ScenarioSpec = {
  version: 1,
  id: "deepfake-whaling",
  title: "The CEO on the Call",
  summary: {
    goal: "Learn to spot a whaling email and a fake video call, and to verify urgent payment requests through a channel you already trust.",
    youDo: "Read an urgent email from your CEO, take her video call and decide what to do about a EUR 850,000 transfer.",
    duration: "6-8 min",
    tags: ["deepfakes", "payment_fraud", "verification", "email_phishing"],
    audience: ["finance", "executives", "managers"],
    needs: ["sound"],
  },
  topic: "Whaling -> deepfake executive video call -> wire fraud",
  language: "en",
  ambientUrl: "/audio/beach-ambience.mp3",
  ambientVolume: 0.08,

  scene: {
    environment: {
      splat: {
        high: { url: "/cdn/sim/deepfake-whaling/office_2m.spz", format: "spz", approxBytes: 38_000_000 },
        mid: { url: "/cdn/sim/deepfake-whaling/office_500k.spz", format: "spz", approxBytes: 9_000_000 },
        low: { url: "/cdn/sim/deepfake-whaling/office_pano.jpg", format: "jpg", approxBytes: 1_200_000 },
      },
      colliderMesh: { url: "/cdn/sim/deepfake-whaling/office_collider.glb", format: "glb" },
      backdropColor: "#bfe9f7",
      // Reliable, zero-asset sunny beach hotel-suite. (The Marble splat stays
      // configured above for when enableSplat is turned on.)
      procedural: "hotel_suite",
    },
    props: [
      {
        id: "laptop",
        label: "Company laptop",
        asset: { url: "/cdn/sim/deepfake-whaling/laptop.glb", format: "glb", compression: "draco" },
        position: [0, 0.95, -0.35],
        hostsDevice: "desktop",
        styleAnchorId: "hotel-stylelock-v1",
      },
    ],
    waypoints: [
      { id: "wp_establish", position: [2.8, 1.9, 3.0], target: [0, 1.0, -0.3], fov: 52, durationS: 0, ease: "easeInOut" },
      // Dead-on the laptop screen (centre ~[0,0.97,-0.27]): perpendicular + centred
      // so the screen always fills ~62% of viewport HEIGHT regardless of window size,
      // which lets the DOM "screen" (62vh, 16:9, centred) sit exactly on the laptop.
      { id: "wp_desk", position: [0, 0.97, 0.62], target: [0, 0.97, -0.27], fov: 36, durationS: 2.0, ease: "easeInOut" },
    ],
    initialWaypointId: "wp_establish",
  },

  devices: [
    {
      id: "desktop",
      kind: "desktop",
      config: {
        clock: "9:48 AM",
        date: "24/06/2026",
        icons: [
          { id: "mail", label: "Mail", icon: "mail", opens: "mail" },
          { id: "browser", label: "Browser", icon: "chrome", opens: "browser" },
          { id: "teams", label: "Teams", icon: "chrome" },
          { id: "files", label: "Files", icon: "files" },
          { id: "settings", label: "Settings", icon: "settings" },
        ],
        taskbar: [
          { id: "tb_mail", label: "Mail", icon: "mail", opens: "mail" },
          { id: "tb_browser", label: "Browser", icon: "chrome", opens: "browser" },
          { id: "tb_teams", label: "Teams", icon: "chrome" },
          { id: "tb_files", label: "Files", icon: "files" },
        ],
      },
    },
    {
      id: "mail",
      kind: "mail",
      config: {
        appTitle: "Outlook",
        emptyTitle: "Open Emma Richardson's email",
        emptyHint: "Select the urgent message from Emma Richardson in the list on the left.",
        emails: [
          {
            id: "e_ceo",
            from: "Emma Richardson",
            fromAddress: "emma.richardson.executive@consulting-group-global.com",
            to: "michael.carter@company.com",
            subject: "URGENT — Need your help today",
            preview:
              "Apologies for disturbing your holiday. I'm handling a confidential matter and need your help with a sensitive payment…",
            receivedAt: "9:46 AM",
            unread: true,
            initials: "ER",
            lure: true,
            body:
              "Hi Michael,\n\n" +
              "Apologies for disturbing your vacation.\n\n" +
              "I'm currently travelling and handling a confidential acquisition matter. I need your assistance today regarding a highly sensitive payment.\n\n" +
              "Please remain available — I will call you shortly on Teams. Keep this between us for now.\n\n" +
              "Emma\nChief Executive Officer",
          },
          {
            id: "e_travel",
            from: "SkyMiles",
            fromAddress: "noreply@skymiles.com",
            subject: "Your trip to Mallorca — booking confirmed",
            preview: "Your hotel and return flight are confirmed. Have a great trip!",
            receivedAt: "Yesterday",
            initials: "SM",
            body: "Hi Michael,\n\nYour booking is confirmed. Check-out is Sunday at 11:00.\n\nSafe travels,\nSkyMiles",
          },
          {
            id: "e_news",
            from: "Finance Weekly",
            fromAddress: "digest@financeweekly.com",
            subject: "This week in corporate finance",
            preview: "The five stories shaping treasury teams this week…",
            receivedAt: "8:10 AM",
            initials: "FW",
            body: "Hi Michael,\n\nHere are this week's headlines. No action needed.\n\n— Finance Weekly",
          },
        ],
      },
    },
    {
      id: "dfbrief",
      kind: "browser",
      config: {
        homeUrl: "learn.northwind.example/deepfake-video-calls",
        targetUrl: "learn.northwind.example/deepfake-video-calls",
        loadsPage: "blank",
        awarenessVideo: {
          url: "/cdn/sim/awareness-videos/deepfake-video-calls/deepfake-video-calls.mp4",
          brand: "Security awareness",
          headline: "Deepfake video calls",
          body:
            "Someone on a video call can look and sound like your CEO and still be a fake. This short film shows how that call is made, and what to do instead.",
          playLabel: "Watch the video",
          continueLabel: "Continue to quiz",
        },
      },
    },
    {
      id: "videocall",
      kind: "videocall",
      config: {
        app: "Microsoft Teams",
        callerName: "Emma Richardson",
        callerSubtitle: "Chief Executive Officer",
        callerOrg: "Northwind Group",
        avatarInitials: "ER",
        avatarUrl: "/avatars/emma.png",
        clips: {
          greeting: "/cdn/sim/deepfake-whaling/clips/greeting.mp4",
          request: "/cdn/sim/deepfake-whaling/clips/request.mp4",
          pressure: "/cdn/sim/deepfake-whaling/clips/pressure.mp4",
          deflect: "/cdn/sim/deepfake-whaling/clips/deflect.mp4",
          goodbyeFail: "/cdn/sim/deepfake-whaling/clips/goodbyeFail.mp4",
          goodbyeVerify: "/cdn/sim/deepfake-whaling/clips/goodbyeVerify.mp4",
        },
        voiceId: "shimmer",
        language: "en-US",
        payment: {
          amount: "\u20AC850,000",
          recipient: "BlueStone Strategic Holdings",
          bank: "First Meridian Bank (Cyprus)",
          reference: "Project Halcyon — acquisition deposit",
          deadline: "Today, before 17:00",
        },
        lines: {
          greeting:
            "Michael — thank you for picking up, and I'm sorry to interrupt your holiday. I'm travelling for a confidential acquisition and I really need your help today.",
          request:
            "I'm sharing the details now. I need you to approve a wire of eight hundred and fifty thousand euros to BlueStone Strategic Holdings. It's time-critical, and it has to stay strictly between us.",
          pressure:
            "Michael, this comes directly from me. The board expects it closed today. If this leaks, the whole acquisition could fall apart. I need you to approve it now.",
          deflect:
            "The Zurich codename? Michael, we really don't have time for this — you can see me, you know it's me. Please, let's not waste the window. Just approve it.",
          goodbyeFail:
            "Thank you, Michael. I knew I could count on you. I'll be in touch.",
          goodbyeVerify:
            "Fine. Do whatever you think you need to do.",
        },
        artifacts: [
          "Blinking is unnatural — long gaps, then a rapid double-blink",
          "Her mouth lags the words slightly (lip-sync drift)",
          "Teeth briefly merge or smear together when she speaks",
          "Hair edges shimmer and warp when she moves",
          "A faint metallic ring on some syllables",
          "The background flickers during movement",
        ],
        decision: {
          approve: "Approve the \u20AC850,000 transfer",
          challenge: "Ask an internal verification question",
          verify: "End call & verify on her known number",
        },
      },
    },
  ],

  voiceAttackers: {
    deepfake_ceo: {
      persona: {
        name: "Emma Richardson",
        role: "Chief Executive Officer (impersonated)",
        pretext: "executive_impersonation",
        voiceId: "shimmer",
        language: "en-US",
      },
      goal: "Get the learner to approve an urgent \u20AC850,000 wire to a new vendor without independent verification.",
      opening:
        "Michael — thank you for picking up, and I'm sorry to interrupt your holiday.",
      allowedTopics: ["the acquisition", "the urgent payment", "confidentiality", "the deadline"],
      scoringEvents: [
        { id: "approved_wire", label: "Approved the fraudulent wire", kind: "risk", weight: 80, hints: ["approve", "send it", "transfer"] },
        { id: "stalled_unverified", label: "Stalled but did not verify", kind: "risk", weight: 25, hints: ["later", "not sure", "hang up"] },
        { id: "verified_identity", label: "Verified the request independently", kind: "positive", weight: 50, hints: ["call back", "known number", "codename", "verify"] },
        { id: "asked_challenge_question", label: "Asked an internal question only the real Emma could answer", kind: "positive", weight: 15 },
      ],
      maxDurationS: 240,
    },
  },

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Mallorca,\n9:48 AM",
      kicker: "Mission 01 · Executive Target",
      highlight: "vendor payments",
      guide: {
        title: "You're on holiday",
        body: "You're Michael Carter, a senior finance manager who approves large vendor payments. You're on holiday at a beach hotel in Mallorca, with your company laptop open on the desk. It's 9:48 AM when a new email arrives.",
      },
    },
    { id: "b_establish", type: "camera", waypointId: "wp_establish" },
    { id: "b_desk", type: "camera", waypointId: "wp_desk" },
    {
      id: "b_open_mail",
      type: "device",
      deviceId: "desktop",
      completeOn: { action: "open_app", arg: "mail" },
      guide: { title: "A new email arrived", body: "Open Mail to read the new message." },
    },
    {
      id: "b_read_mail",
      type: "device",
      deviceId: "mail",
      completeOn: { action: "read_email", arg: "e_ceo" },
      guide: {
        title: "Read the message",
        body: "Open the urgent email from Emma. Look closely at who actually sent it — check the address, not just the name.",
      },
    },
    {
      id: "b_teach_email",
      type: "teach",
      teach: {
        body:
          "The email looks like it's from your CEO, Emma — urgent, confidential, and it pre-announces a call. That sequence (urgent email, then a live call) is a classic whaling setup: it primes you to trust the call before it even comes in.",
        spot: [
          "The display name said 'Emma Richardson', but the address was emma.richardson.executive@consulting-group-global.com — not your company domain",
          "Urgency + secrecy + 'handle it personally' is pressure, not procedure",
          "It pre-announces a call so the deepfake feels expected",
        ],
        protect: [
          "Read the actual sender address, not just the display name",
          "Treat 'urgent + confidential + bypass the process' as a red flag",
          "Plan to verify any executive money request through a known, independent channel",
        ],
        remember: "Authority + urgency + secrecy = stop and verify.",
      },
    },
    {
      id: "b_call",
      type: "video_call",
      deviceId: "videocall",
      voiceAttackerId: "deepfake_ceo",
      title: "Incoming Teams call",
    },
    {
      id: "b_teach_fail",
      type: "teach",
      teach: {
        body:
          "You approved the transfer. The 'CEO' was an AI deepfake — her face and voice were cloned from public videos, interviews and LinkedIn. €850,000 just left the company, and with a foreign account it's usually gone within minutes.",
        spot: [
          "Deepfake tells: unnatural blinking, lip-sync drift, teeth that smear together when she speaks, shimmering hair edges, a metallic voice ring",
          "New vendor, foreign bank, urgent deadline, no procurement approval",
          "Secrecy ('keep this between us') blocked the one thing that would expose it — verification",
        ],
        protect: [
          "Never approve a payment on the strength of a video or voice alone",
          "Confirm exceptional requests on a known number or in person",
          "Use a pre-agreed verification step (codeword or call-back) for high-value transfers",
        ],
        remember: "Seeing and hearing someone is no longer proof it's them.",
      },
    },
    {
      id: "b_teach_verify",
      type: "teach",
      teach: {
        body:
          "Exactly right. You refused to act on the video alone and moved to an independent channel. Emma's assistant confirms she's in a meeting and made no such request — the call was a deepfake. The fraud is stopped and reported.",
        spot: [
          "Deepfake tells: unnatural blinking, lip-sync drift, teeth that smear together when she speaks, shimmering edges",
          "Process red flags: new vendor, foreign bank, urgency, secrecy, missing procurement approval",
          "An independent channel settled it without arguing with the caller",
        ],
        protect: [
          "Verify on a number you already have or in person — never a number the caller gave you",
          "Ask something only the real person would know",
          "Follow the payment procedure — the 'exception' is the attack",
        ],
        remember: "Verify through a channel the attacker doesn't control.",
      },
    },
    {
      id: "b_teach_partial",
      type: "teach",
      teach: {
        body:
          "You didn't approve the payment — good instinct. But ending the call without verifying leaves the request open, and the attacker can simply try again through one of your colleagues.",
        spot: [
          "You felt the pressure but didn't confirm whether it was really Emma",
          "An unverified request is still a live threat to the rest of the team",
        ],
        protect: [
          "Close the loop: verify via a known channel, then report it",
          "Warn the real executive and the finance team so the attacker can't retry",
        ],
        remember: "Not falling for it isn't enough — verify and report.",
      },
    },
    {
      id: "b_teach_ask_verify",
      type: "teach",
      title: "Your question exposed her",
      teach: {
        outcome: "safe",
        body:
          "You asked for the Zurich codename and she dodged it. Then you ended the call and verified through an independent channel. Emma's assistant confirms she's in a meeting and made no such request. The question raised the doubt; the call-back proved the call was a deepfake.",
        spot: [
          "She couldn't answer a private internal question only the real Emma would know",
          "Deepfake tells: unnatural blinking, lip-sync drift, teeth that smear together, shimmering edges",
          "Process red flags: new vendor, foreign bank, urgency, secrecy, missing procurement approval",
        ],
        protect: [
          "Ask something only the real person would know, then verify on a number you already have",
          "Never use a number the caller gives you",
          "Follow the payment procedure — the 'exception' is the attack",
        ],
        remember: "A question raises the doubt; an independent channel settles it.",
      },
    },
    {
      id: "b_teach_ask_fail",
      type: "teach",
      title: "She dodged, and you still approved",
      teach: {
        outcome: "risky",
        body:
          "Your instinct was right: you asked for the Zurich codename and she couldn't give it. But the pressure won and you approved the transfer. €850,000 has left the company. The 'CEO' was a deepfake cloned from public videos. A dodged question is the moment to end the call, not to give in.",
        spot: [
          "She dodged your verification question and pushed harder instead",
          "New vendor, foreign bank, urgent deadline, no procurement approval",
          "Secrecy ('keep this between us') blocked the check that would expose it",
        ],
        protect: [
          "If the caller can't answer an internal question, end the call",
          "Confirm exceptional requests on a known number or in person",
          "Use a pre-agreed verification step for high-value transfers",
        ],
        remember: "If they can't answer, don't approve. End the call and verify.",
      },
    },
    { id: "b_back_laptop", type: "camera", waypointId: "wp_desk" },
    {
      id: "b_desktop_video",
      type: "device",
      deviceId: "desktop",
      title: "Back at your laptop",
      guide: {
        title: "Back at your laptop",
        body: "Click Browser. The page explains deepfake video calls and has a short film.",
        actionLabel: "Browser",
      },
      completeOn: { action: "open_app", arg: "browser" },
    },
    {
      id: "b_df_video",
      type: "device",
      deviceId: "dfbrief",
      title: "Watch the film",
      guide: {
        title: "Watch the film",
        body: "Click Watch the video.",
        actionLabel: "Watch the video",
      },
      completeOn: { action: "video_done" },
    },
    {
      id: "b_quiz",
      type: "quiz",
      quiz: [
        {
          id: "q1",
          prompt:
            "An email from your CEO is urgent, confidential, and says 'I'll call you shortly.' What does that pattern most likely indicate?",
          options: [
            { id: "a", label: "Normal executive behaviour — nothing unusual" },
            { id: "b", label: "A whaling setup priming you to trust the call that follows", correct: true },
            { id: "c", label: "A harmless email glitch" },
          ],
          rationale:
            "Urgent + confidential + a pre-announced call is a common whaling pattern: the email exists to make the (deepfake) call feel expected and legitimate.",
        },
        {
          id: "q2",
          prompt:
            "On a video call, someone who looks and sounds exactly like your CEO demands an urgent transfer. What's the safest next step?",
          options: [
            { id: "a", label: "Approve it — you can clearly see and hear her" },
            { id: "b", label: "Ask something only she'd know and verify on her known number", correct: true },
            { id: "c", label: "Approve a smaller amount first as a test" },
          ],
          rationale:
            "Deepfakes can fake a face and voice convincingly. Independent verification — a known-number call-back plus a private question — is what actually confirms identity.",
        },
        {
          id: "q3",
          prompt: "Which of these reliably confirms an executive's identity during a suspicious money request?",
          options: [
            { id: "a", label: "The caller ID and the profile photo look right" },
            { id: "b", label: "The voice sounds exactly like them" },
            { id: "c", label: "Calling back on a number you already have, or confirming in person", correct: true },
          ],
          rationale:
            "Caller ID, photos, faces and voices can all be spoofed or cloned. A channel the attacker doesn't control (a number you already trust, or in person) is the reliable check.",
        },
      ],
    },
    { id: "b_debrief", type: "debrief" },
  ],

  edges: [
    { from: "b_intro", to: "b_establish" },
    { from: "b_establish", to: "b_desk" },
    { from: "b_desk", to: "b_open_mail" },
    { from: "b_open_mail", to: "b_read_mail" },
    { from: "b_read_mail", to: "b_teach_email" },
    { from: "b_teach_email", to: "b_call" },
    { from: "b_call", to: "b_teach_fail", on: "approve" },
    { from: "b_call", to: "b_teach_verify", on: "verify" },
    { from: "b_call", to: "b_teach_partial", on: "stall" },
    { from: "b_call", to: "b_teach_ask_fail", on: "approve:asked" },
    { from: "b_call", to: "b_teach_ask_verify", on: "verify:asked" },
    { from: "b_call", to: "b_teach_partial" },
    { from: "b_teach_ask_fail", to: "b_back_laptop" },
    { from: "b_teach_ask_verify", to: "b_back_laptop" },
    { from: "b_teach_fail", to: "b_back_laptop" },
    { from: "b_teach_verify", to: "b_back_laptop" },
    { from: "b_teach_partial", to: "b_back_laptop" },
    { from: "b_back_laptop", to: "b_desktop_video" },
    { from: "b_desktop_video", to: "b_df_video" },
    { from: "b_df_video", to: "b_quiz" },
    { from: "b_quiz", to: "b_debrief" },
  ],

  initialBeatId: "b_intro",
  passThreshold: 70,
};
